import { IconTile } from "@/components/rail-kit/IconTile";
import {
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTriggerBase,
} from "@/components/ui/accordion";
import { FilesRailDropTarget } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/FilesRailDropTarget";
import {
  FILES_RAIL_SECTION_HOVER_GROUP,
  filesRailSectionHoverRevealTargetClassName,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/filesRailHoverRevealClassName";
import type { WorkspaceDropPayload } from "@/pages/workspaces/components/WorkspaceDropZone";
import { cn } from "@/utils";
import { ChevronDown, type LucideIcon } from "lucide-react";
import React, { useState } from "react";

const PAGE_SIZE = 5;

export const railHeadingLabelClassName =
  "min-w-0 flex-1 truncate font-mono text-2xs font-medium uppercase tracking-[0.08em]";

export function RailGroup({
  groupId,
  label,
  icon: Icon,
  rightSlot,
  noCap,
  items,
  emptyState,
  onHeaderClick,
  headerActive,
  dropTargetWorkspaceId,
  onOsFilesDrop,
  hoverRevealRightSlot,
  onContextMenu,
  anchor,
}: {
  groupId: string;
  label: string;
  icon: LucideIcon;
  rightSlot?: React.ReactNode;
  noCap?: boolean;
  items: React.ReactNode[];
  emptyState?: React.ReactNode;
  /** Label click — e.g. My Workspace navigates to the private workspace page. */
  onHeaderClick?: () => void;
  headerActive?: boolean;
  /** Workspace root target for in-app moves and OS uploads. */
  dropTargetWorkspaceId?: string;
  /** When set, section content accepts OS file drops with a rust inset ring. */
  onOsFilesDrop?: ((payload: WorkspaceDropPayload) => void) | null;
  /** Hide `rightSlot` until the section header or its children are hovered. */
  hoverRevealRightSlot?: boolean;
  /** Right-click handler on the header — opens the section's action menu. */
  onContextMenu?: (e: React.MouseEvent) => void;
  /** Onboarding target name for the header. */
  anchor?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const hasMore = !noCap && items.length > PAGE_SIZE;
  const visible = noCap || expanded ? items : items.slice(0, PAGE_SIZE);
  const remaining = items.length - PAGE_SIZE;

  const listContent =
    items.length === 0 && emptyState ? (
      emptyState
    ) : (
      <>
        {visible}
        {hasMore && (
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            aria-expanded={expanded}
            className="flex min-h-7 w-full items-center gap-2.5 rounded-md px-2 text-left text-n-50 hover:bg-n-05 hover:text-n-70"
          >
            <ChevronDown
              className={cn(
                "size-4 shrink-0 transition-transform duration-150 ease-out",
                expanded && "rotate-180",
              )}
            />
            <span className="text-sm">
              {expanded ? "Show less" : `Show ${remaining} more`}
            </span>
          </button>
        )}
      </>
    );

  const listInner = <div className="flex flex-col pl-1">{listContent}</div>;

  const header = (
    <AccordionHeader className="flex">
      <div
        onContextMenu={onContextMenu}
        data-onboarding-target={anchor}
        className={cn(
          "relative flex min-h-7 w-full items-center gap-2 px-2",
          headerActive && "rounded-md bg-n-05",
        )}
      >
        {onHeaderClick ? (
          // Navigable header (e.g. "My Workspace"): the chevron toggles the
          // section, the icon+label navigate.
          <>
            <AccordionTriggerBase asChild>
              <button
                type="button"
                aria-label={`Toggle ${label} section`}
                className="group group/row -m-1 inline-flex shrink-0 rounded-sm p-1 hover:bg-n-10"
              >
                {/* The tile is what separates one top-level run from the
                    next: a filled coin anchors the eye at each head, so the
                    rail parses as sections without any rules between them. */}
                <IconTile icon={Icon} />
              </button>
            </AccordionTriggerBase>
            <button
              type="button"
              onClick={onHeaderClick}
              className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
            >
              <span
                className={cn(
                  railHeadingLabelClassName,
                  "text-n-50 hover:text-n-70",
                  headerActive && "text-n-90",
                )}
              >
                {label}
              </span>
            </button>
          </>
        ) : (
          // Default: the whole chevron+icon+label area is the toggle; the
          // hover background signals it's interactive.
          <AccordionTriggerBase asChild>
            <button
              type="button"
              aria-label={`Toggle ${label} section`}
              className={cn(
                "group group/row -mx-1 flex min-w-0 flex-1 items-center gap-2.5 rounded-md px-1 py-1 text-left hover:bg-n-05",
                // Clearance for the overlaid controls, so a long label
                // truncates before running under them.
                hoverRevealRightSlot && "pr-14",
              )}
            >
              {/* Same hover box as the tile button on workspace heads
                  (RailWorkspaceExpandableRow) — a span, since this head is
                  already one button. One disclosure gesture across rails. */}
              <span className="-m-1 inline-flex shrink-0 rounded-sm p-1 hover:bg-n-10">
                <IconTile icon={Icon} />
              </span>
              <span
                className={cn(
                  railHeadingLabelClassName,
                  "text-n-50 group-hover/row:text-n-70",
                  headerActive && "text-n-90",
                )}
              >
                {label}
              </span>
            </button>
          </AccordionTriggerBase>
        )}
        {rightSlot ? (
          <span
            className={cn(
              "ml-auto shrink-0",
              // Hover-revealed controls ride OVER the head's right edge
              // (the DS eyebrow rule): the toggle underneath keeps the full
              // row, so its hover wash matches a control-less head.
              hoverRevealRightSlot &&
                cn(
                  "absolute inset-y-0 right-1 flex items-center",
                  filesRailSectionHoverRevealTargetClassName,
                ),
            )}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {rightSlot}
          </span>
        ) : null}
      </div>
    </AccordionHeader>
  );

  return (
    <AccordionItem
      value={groupId}
      className={cn(
        "border-b-0",
        // The reveal scope for hover-revealed head controls. It must live
        // here, not only on the drop-target wrapper — a section without a
        // drop target otherwise has no group and its controls never show.
        hoverRevealRightSlot && FILES_RAIL_SECTION_HOVER_GROUP,
      )}
    >
      {dropTargetWorkspaceId ? (
        <FilesRailDropTarget
          workspaceId={dropTargetWorkspaceId}
          parentPath=""
          onOsDrop={onOsFilesDrop ?? null}
          className={cn(
            "flex flex-col rounded-md",
            hoverRevealRightSlot && FILES_RAIL_SECTION_HOVER_GROUP,
          )}
        >
          {header}
          <AccordionContent className="pb-2 pt-0">{listInner}</AccordionContent>
        </FilesRailDropTarget>
      ) : (
        <>
          {header}
          <AccordionContent className="pb-2 pt-0">{listInner}</AccordionContent>
        </>
      )}
    </AccordionItem>
  );
}
